/**
 * Utility classes for consistent theming across frontend packages.
 *
 * These classes use CSS custom properties that get updated when themes switch.
 * All classes are theme-agnostic and work across sj-legacy, atlas-light, and atlas-dark.
 *
 * Usage:
 *   import { themeClasses } from 'sj-ui-toolkit/utils/themeClasses';
 *   <div className={themeClasses.bgSurface0}>Content</div>
 */

export const themeClasses = {
  scrollbar: "atlas-scrollbar",

  // ============================================
  // SURFACE BACKGROUNDS
  // ============================================
  bgSurface0: "bg-surface-0",
  bgSurface50: "bg-surface-50",
  bgSurface100: "bg-surface-100",
  bgSurface200: "bg-surface-200",
  bgSurface300: "bg-surface-300",
  bgSurface400: "bg-surface-400",
  bgSurface500: "bg-surface-500",
  bgSurface600: "bg-surface-600",
  bgSurface700: "bg-surface-700",
  bgSurface800: "bg-surface-800",
  bgSurface900: "bg-surface-900",
  bgSurface950: "bg-surface-950",
  bgSurfaceHover: "bg-surface-hover",

  // ============================================
  // GRAY BACKGROUNDS
  // ============================================
  bgGray0: "bg-gray-0",
  bgGray50: "bg-gray-50",
  bgGray100: "bg-gray-100",
  bgGray200: "bg-gray-200",
  bgGray300: "bg-gray-300",
  bgGray400: "bg-gray-400",
  bgGray500: "bg-gray-500",
  bgGray600: "bg-gray-600",
  bgGray700: "bg-gray-700",
  bgGray800: "bg-gray-800",
  bgGray900: "bg-gray-900",
  bgGray950: "bg-gray-950",

  // ============================================
  // ELEVATION BACKGROUNDS
  // ============================================
  bgElevation0: "bg-elevation-0",
  bgElevation1: "bg-elevation-1",
  bgElevation2: "bg-elevation-2",
  bgElevation3: "bg-elevation-3",

  // ============================================
  // SEMANTIC BACKGROUNDS
  // ============================================
  bgPrimary: "bg-primary",
  bgPrimaryDarker: "bg-primary-darker",
  bgError: "bg-error",
  bgWarning: "bg-warning",

  // ============================================
  // INDIGO DATAVIZ BACKGROUNDS
  // ============================================
  bgIndigoDataviz1: "bg-indigo-dataviz-1",
  bgIndigoDataviz2: "bg-indigo-dataviz-2",
  bgIndigoDataviz3: "bg-indigo-dataviz-3",
  bgIndigoDataviz4: "bg-indigo-dataviz-4",
  bgIndigoDataviz5: "bg-indigo-dataviz-5",

  // ============================================
  // TEXT COLORS
  // ============================================
  textSurface0: "text-surface-0",
  textSurface50: "text-surface-50",
  textSurface100: "text-surface-100",
  textSurface200: "text-surface-200",
  textSurface300: "text-surface-300",
  textSurface400: "text-surface-400",
  textSurface500: "text-surface-500",
  textSurface600: "text-surface-600",
  textSurface700: "text-surface-700",
  textSurface800: "text-surface-800",
  textSurface900: "text-surface-900",
  textSurface950: "text-surface-950",

  textGray0: "text-gray-0",
  textGray50: "text-gray-50",
  textGray100: "text-gray-100",
  textGray200: "text-gray-200",
  textGray300: "text-gray-300",
  textGray400: "text-gray-400",
  textGray500: "text-gray-500",
  textGray600: "text-gray-600",
  textGray700: "text-gray-700",
  textGray800: "text-gray-800",
  textGray900: "text-gray-900",
  textGray950: "text-gray-950",

  textPrimary: "text-primary",
  textPrimaryDarker: "text-primary-darker",
  textSecondary: "text-secondary",
  textError: "text-error",
  textErrorDark: "text-error", // Using same as textError (no darker variant available)
  textWarning: "text-warning",
  textSuccess: "text-success", // Temporary: uses --temp-success-color until added to themes

  // ============================================
  // INDIGO DATAVIZ TEXT COLORS
  // ============================================
  textIndigoDataviz1: "text-indigo-dataviz-1",
  textIndigoDataviz2: "text-indigo-dataviz-2",
  textIndigoDataviz3: "text-indigo-dataviz-3",
  textIndigoDataviz4: "text-indigo-dataviz-4",
  textIndigoDataviz5: "text-indigo-dataviz-5",

  // ============================================
  // BORDER COLORS
  // ============================================
  borderSurface300: "border-surface-300",
  borderSurface400: "border-surface-400",
  borderSurfaceBorder: "border-surface-border",
  borderGray300: "border-gray-300",
  borderGray400: "border-gray-400",
  borderPrimary: "border-primary",

  // ============================================
  // HOVER STATES
  // ============================================
  hoverBgSurfaceHover: "hover:bg-surface-hover",
  hoverBgSurface100: "hover:bg-surface-100",
  hoverBgSurface200: "hover:bg-surface-200",
  hoverBgGray100: "hover:bg-gray-100",
  hoverBgGray200: "hover:bg-gray-200",
  hoverBgGray300: "hover:bg-gray-300",
  hoverBgPrimary: "hover:bg-primary",

  // ============================================
  // LINKS
  // ============================================
  linkPrimary: "text-primary hover:underline",

  // ============================================
  // ICONS (use text color utilities)
  // ============================================
  iconError: "text-error",
  iconWarning: "text-warning",
  iconSuccess: "text-success", // Temporary: uses --temp-success-color until added to themes

  // ============================================
  // LAYOUT
  // ============================================
  mainContentContainer: "px-4 pt-4",
  lessonContentContainer: "pt-4 h-full",
  guestLessonContainer: "pt-4 pl-4 h-full",

  // ============================================
  // CONTENT PANELS
  // ============================================
  flexLayout: "flex-layout",
  flexMain: "flex-main",
  splitter: "splitter",
  leftPanelContainer: "left-panel-container",
  rightPanelContainer: "right-panel-container",
  rightPanelBody: "right-panel-body",
  rightPanelFooter: "right-panel-footer",

  // ============================================
  // Margin/Padding/Gap Utilities
  // ============================================
  splitterGap: "splitter-gap",
  fullHeightBelowNavbar: "full-height-below-navbar",
  maxHeightBelowNavbar: "max-height-below-navbar",
  largePaddedContainer: "large-padded-container",
} as const;

// Type export for autocomplete
export type ThemeClassName = (typeof themeClasses)[keyof typeof themeClasses];
export function C(...classes: (ThemeClassName | string)[]): string {
  return classes.join(" ");
}
